<template>
  <!-- Error Modal -->

  <div class="w-full space-y-8">
    <!-- Success Summary -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <div class="text-left space-y-3">
        <p class="font-semibold text-lg">Submission successful</p>

        <ul class="mt-1 space-y-3 text-sm">
          <li class="space-y-1">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-sm">DOI:</span>
            </div>
            <a
              class="cursor-pointer text-blue-800 hover:underline break-all"
              :href="doiUrlEncoded"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ doiUrl }}
            </a>
          </li>

          <li class="space-y-1">
            <p class="font-semibold text-sm">VUO Dataset:</p>
            <a
              class="cursor-pointer text-blue-800 hover:underline break-all"
              :href="vuoUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ vuoUrl }}
            </a>
          </li>
        </ul>
      </div>
    </section>

    <!-- Citation -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <div class="flex justify-between items-center">
        <p class="font-semibold">Citation</p>
        <div class="text-sm">
          <span class="mr-2">style :</span>
          <select
            class="border-2 border-gray-200 rounded-xl px-2 py-1 bg-white"
            id="citation-style-select"
            @change="createCitation($event.target.value)"
          >
            <option value="apa" default>APA</option>
            <option value="mla">MLA</option>
            <option value="chicago">Chicago</option>
            <option value="harvard">Harvard</option>
            <option value="vancouver">Vancouver</option>
            <option value="ieee">IEEE</option>
            <option value="bibtex">Bibtex</option>
          </select>
        </div>
      </div>

      <div class="relative mt-3">
        <p
          id="citation-text"
          class="text-sm bg-gray-50 border border-gray-200 p-3 rounded-lg"
        >
          <span>{{ citation }}</span>
        </p>
        <img
          class="h-5 absolute top-3 right-3 cursor-pointer"
          :src="copy_clipboard"
          title="copy to clipboard"
          @click="copyToClipboard"
        />
      </div>
    </section>

    <!-- Description -->
    <section
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50"
    >
      <div class="text-left space-y-3">
        <div class="flex items-center gap-2 font-semibold">
          <span>Description</span>
        </div>
        <textarea
          class="w-full text-sm text-gray-700 border-2 border-gray-200 rounded-xl p-3 resize-none bg-gray-50"
          rows="10"
          disabled
          v-model="formState.description"
        ></textarea>
      </div>
    </section>

    <!-- Actions -->
    <div class="my-8 w-full flex justify-center gap-4">
      <button
        class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        @click="newSubmission"
      >
        <Icon icon="mdi:add-circle-outline" class="text-mocha-mousse"></Icon>
        <span>New Submission</span>
      </button>
      <button
        :disabled="!buttonEnabled"
        class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-blue-100 to-blue-200 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        @click="viewSubmission"
      >
        <Icon icon="mdi:eye-outline" class="text-blue-700"></Icon>
        <span>View Submission</span>
        <span v-if="!buttonEnabled">({{ seconds }})</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useLogger } from "@/stores/useLogger";
import copyToClipboardFn from "@/utils/clipboard";
import { useUpload } from "@/composables/useUpload";
import { useCitationComposer } from "@/composables/useCitation";
import { useFormStore } from "@/stores/FormStore";
import { useLoadingService } from "@/services/LoadingService"; // ⬅️ loading service
import { Icon } from "@iconify/vue";

const logger = useLogger();
const formStore = useFormStore();
const formState = formStore.formState;

const buttonEnabled = ref(false);
const seconds = ref(10);

const copy_clipboard = ref(null);

// Upload workflow
const { isLoading, error, error_message, upload_response, runUpload } =
  useUpload();
const { citation, doiUrl, doiUrlEncoded, vuoUrl, compose } =
  useCitationComposer();

// Loading service wiring
const loadingSvc = useLoadingService();
const { isVisible, title, progress } = storeToRefs(loadingSvc);

// Optional: reflect isLoading changes to the overlay if you want automatic toggling
// (we still control it manually around runUpload for precise titles)
watch(isLoading, (val) => {
  if (val) {
    loadingSvc.start("final-upload", { title: "Uploading dataset…" });
  } else {
    loadingSvc.stop("final-upload");
  }
});

onMounted(async () => {
  copy_clipboard.value = `${process.env.BASE_URL}svg/copy-clipboard.svg`;

  const taskId = "final-upload";
  try {
    // Explicitly start overlay with a nice title
    loadingSvc.start(taskId, { title: "Uploading dataset…" });

    const response = await runUpload(formState);

    // Optional: update overlay title right before composing citation
    loadingSvc.update(taskId, { title: "Finalizing…" });

    // Build the citation using the single-object compose signature
    const r = response || upload_response.value || {};
    compose({
      title: formState.title,
      upload_type: formState.upload_type,
      uploaders: formState.uploaders,
      doi: r.doi,
      investigation_id: r.vuo_id,
      environment:
        localStorage.getItem("development") === "false" ? "prod" : "dev",
      citationType: "apa",
    });

    const timer = setInterval(() => {
      seconds.value--;

      if (seconds.value <= 0) {
        clearInterval(timer);
        buttonEnabled.value = true;
      }
    }, 1000);
  } catch (e) {
    logger.log("Upload failed", e);
  } finally {
    // Ensure overlay stops even if errors happen
    loadingSvc.stop(taskId);
  }
});

const router = useRouter();
const route = useRoute();

// allow back nav only when error occurs (router guard reads this)
watch(error, (val) => {
  if (val) {
    router.replace({
      name: "confirmation",
      query: { ...route.query, allowBack: "1" },
    });
  }
});

function createCitation(citationType) {
  const r = upload_response.value || {};
  if (!r) return;
  try {
    compose({
      title: formState.title,
      upload_type: formState.upload_type,
      uploaders: formState.uploaders,
      doi: r.doi,
      investigation_id: r.vuo_id,
      environment:
        localStorage.getItem("development") === "false" ? "prod" : "dev",
      citationType,
    });
  } catch (e) {
    logger.log("Failed to build citation", e);
  }
}

async function copyToClipboard() {
  if (!citation.value) return;
  await copyToClipboardFn(citation.value);
}

const goToPrevPage = () => {
  logger.log("button_clicked", {
    metadata: { button: "back_to_confirmation" },
  });
  router.push({ name: "submit" });
};

const newSubmission = () => {
  router.replace({ name: "new" });
};

const viewSubmission = () => {
  const r = upload_response.value || {};
  console.log("[viewSubmission] upload_response:", r);

  if (!r.vuo_id) {
    console.error("[viewSubmission] No vuo_id in response:", r);
    return;
  }

  console.log(
    "[viewSubmission] Navigating to showDataset with token:",
    r.vuo_token,
  );

  router.replace({
    name: "showDataset",
    params: { investigation_id: r.vuo_id },
  });
};
</script>
